export const parseCurrencyValue = (value) => {
  const normalized = String(value || '0')
    .replace(/[^\d,.-]/g, '')
    .replace(/\./g, '')
    .replace(',', '.');

  return Number(normalized) || 0;
};

export const buildFallbackSupplierSections = ({ offer, suppliers, products, supplierProducts = [] }) => {
  if (!offer) return [];

  const supplierPool = suppliers.filter((supplier) => supplier.status !== 'Inactive');
  const totalNumeric = parseCurrencyValue(offer.total);
  const itemsNeeded = Math.max(1, Math.min(offer.items || 1, 3));
  const startIndex = Math.abs(String(offer.id).split('').reduce((sum, char) => sum + char.charCodeAt(0), 0)) % products.length;
  const chosenProducts = Array.from({ length: itemsNeeded }, (_, index) => products[(startIndex + index) % products.length]);
  const allocations = chosenProducts.map((_, index) => {
    if (index === itemsNeeded - 1) return null;
    const ratio = itemsNeeded === 1 ? 1 : itemsNeeded === 2 ? [0.58, 0.42][index] : [0.46, 0.32, 0.22][index];
    return Math.round(totalNumeric * ratio);
  });
  const allocated = allocations.filter(Boolean).reduce((sum, value) => sum + value, 0);
  allocations[itemsNeeded - 1] = Math.max(totalNumeric - allocated, 0);

  const grouped = new Map();
  chosenProducts.forEach((product, index) => {
    const primarySupplierProduct = supplierProducts.find((sp) => sp.productId === product.id);
    const productPrice = primarySupplierProduct?.avgItemPrice || 0;
    const supplierName = supplierPool.find((supplier) => supplier.id === primarySupplierProduct?.supplierId)?.name
      || supplierPool[index % supplierPool.length]?.name;
    const qty = Math.max(1, Math.round((allocations[index] || productPrice) / Math.max(productPrice, 1)));
    const unitPrice = Math.max(1, Math.round((allocations[index] || productPrice) / qty));
    const subtotal = qty * unitPrice;
    const costFromSupplier = primarySupplierProduct?.avgItemPrice ?? Math.max(1, Math.round(unitPrice * 0.62));
    const margin = costFromSupplier > 0 ? Math.round(((unitPrice / costFromSupplier) - 1) * 1000) / 10 : 0;

    if (!grouped.has(supplierName)) {
      grouped.set(supplierName, []);
    }

    grouped.get(supplierName).push({
      row: grouped.get(supplierName).length + 1,
      description: product.name,
      notes: 'Standard branding setup confirmed for production.',
      qty,
      unitPrice,
      costFromSupplier,
      baseInternalPrice: costFromSupplier,
      productionCost: 0,
      deliveryCost: 0,
      margin,
      total: subtotal,
    });
  });

  return Array.from(grouped.entries()).map(([name, items], index) => ({
    name,
    number: index + 1,
    status: offer.type === 'Order' ? 'Production' : 'Proof Pending',
    poSent: offer.date,
    items: items.map((item, itemIndex) => ({ ...item, row: itemIndex + 1 })),
  }));
};

export const getOfferSupplierSections = ({ offerId, offers, orderSuppliers, suppliers, products, supplierProducts = [] }) => {
  const offer = offers.find((item) => item.id === offerId);
  return orderSuppliers[offerId] || buildFallbackSupplierSections({ offer, suppliers, products, supplierProducts });
};

export const getOfferLineItems = ({
  offerId, offers, orderSuppliers, suppliers, products, supplierProducts = [], services = [], logisticsItems = [],
}) => (
  getOfferSupplierSections({ offerId, offers, orderSuppliers, suppliers, products, supplierProducts }).flatMap((section) =>
    section.items.map((item, index) => {
      const lineType = item.lineType || 'Product';
      const sectionSupplierId = suppliers.find((supplier) => supplier.name === section.name)?.id;
      // Legacy fallback sections never carry lineType and are always
      // fabricated from `products`, so this re-link only applies there.
      const matchedProduct = lineType === 'Product' ? (
        products.find((product) => (
          product.name === item.description
          && supplierProducts.some((sp) => sp.productId === product.id && sp.supplierId === sectionSupplierId)
        )) || products.find((product) => product.name === item.description)
      ) : null;
      const matchedSupplierProduct = supplierProducts.find((sp) => (
        sp.productId === matchedProduct?.id && sp.supplierId === sectionSupplierId
      ));
      const matchedService = lineType === 'Service' ? (
        (item.serviceId != null ? services.find((s) => s.id === item.serviceId) : null)
        || services.find((s) => s.name === item.description)
      ) : null;
      const matchedLogisticsItem = lineType === 'Logistics' ? (
        (item.logisticsId != null ? logisticsItems.find((l) => l.id === item.logisticsId) : null)
        || logisticsItems.find((l) => l.name === item.description)
      ) : null;

      return {
        id: Number(`${section.number}${index + 1}${offerId.length}`),
      lineType,
      serviceId: matchedService?.id ?? null,
      logisticsId: matchedLogisticsItem?.id ?? null,
      linkedToLineId: item.linkedToLineId ?? null,
      productId: matchedProduct?.id || null,
      itemId: item.itemId,
      supplierName: section.name,
      productName: item.description,
      article: item.article || matchedSupplierProduct?.supplierItemNumber || '',
      quantity: item.qty,
      price: item.unitPrice,
      customerPrice: item.customerPrice ?? item.unitPrice,
      internalPrice: item.internalPrice ?? item.costFromSupplier ?? 0,
      discount: item.discount ?? 0,
      internalLineTotal: item.internalLineTotal ?? ((item.internalPrice ?? item.costFromSupplier ?? 0) * item.qty),
      lineTotal: item.total,
      notes: item.notes || null,
      files: item.files || [],
    };
    }),
  )
);
